import { createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { MembershipUpgradeRequest, MembershipUpgradeResponse } from '../../common/models/StripePayment';
import * as memberAPI from '../../api/memberAPI';
import type { RootState } from '../storeSetup';
import { getMemberInformation } from './onboardingThunk';
import {
  EventObject, RequestAction, RequestEventDetailProps, RequestSubtopic,
} from '../../common/EventTrackingTypes';

export type MembershipUpgradeError = {
  message: string;
  code?: string;
};

export type UpgradeMembershipPayload = MembershipUpgradeRequest & {
  trackRequest: (
    subtopic: RequestSubtopic,
    action: RequestAction,
    object: EventObject,
    errorEventDetailProps: RequestEventDetailProps,
  ) => void;
};

export const upgradeMembership = createAsyncThunk<
MembershipUpgradeResponse,
UpgradeMembershipPayload,
{
  rejectValue: MembershipUpgradeError;
  state: RootState
}>(
  'membership/upgrade',
  async (payload: UpgradeMembershipPayload, { getState, rejectWithValue, dispatch }) => {
    const { onboarding } = getState();
    const { memberId } = onboarding.memberSession;
    const {
      firstName, lastName, email, companyName,
    } = onboarding.memberInformation;

    const cardholderNameParts = payload.cardholderName.trim().split(/\s+/);
    const cardholderFirstName = cardholderNameParts[0] || '';
    const cardholderLastName = cardholderNameParts.slice(1).join(' ') || cardholderNameParts[0] || '';

    const resolvedFirstName = firstName?.trim() || cardholderFirstName;
    const resolvedLastName = lastName?.trim() || cardholderLastName;
    try {
      const response = await memberAPI.upgradeMembership({
        userId: memberId,
        email: email || '',
        firstName: resolvedFirstName,
        lastName: resolvedLastName,
        // When the member has no company name, fall back to their name so the
        // upgrade is never blocked. We intentionally do not persist this to the
        // member's company record — the company name stays empty in our DB.
        companyName: companyName?.trim() || `${resolvedFirstName} ${resolvedLastName}`.trim(),
        zip: payload.zipCode,
        billingFrequency: payload.billingFrequency,
        stripePaymentMethodId: payload.paymentMethodId,
        promoCode: payload.promoCode,
        totalAmount: payload.totalAmount,
      });

      // Refresh member information through the enriched thunk (not a raw API call) so
      // the SN master/sub-account grants (Wallet, purchase-reporting, payout access)
      // are re-fetched instead of being cleared to undefined.
      await dispatch(getMemberInformation({ id: memberId, trackRequest: payload.trackRequest }));

      return response;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.data) {
        const { message, code } = error.response.data;
        return rejectWithValue({ message: message || 'An unexpected error occurred. Please try again.', code });
      }
      return rejectWithValue({ message: 'An unexpected error occurred. Please try again.' });
    }
  },
);
